import React from 'react';
import { ChevronDown, ChevronRight, Link2, Search } from 'lucide-react';

import { adminContent } from 'services/api';
import type {
  QuestionLibraryCatalogCourse,
  QuestionLibraryCourseCatalog,
  QuestionLibraryCourseTree,
  QuestionLibraryTreeModule,
} from 'services/api/adminContentClient';
import { Input } from 'components/ui/input';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { cn } from 'lib/utils';
import CourseJumpLinks from '../CourseJumpLinks';

import {
  type BrowserCourseId,
  UNASSIGNED_COURSE,
  formatQuestionTypeSlug,
  groupCatalogCourses,
  plainQuestionLabel,
} from './navigatorUtils';

export type QuestionCourseBrowserProps = {
  courseId: BrowserCourseId | null;
  openModuleId: number | null;
  selectedQuestionId: number | null;
  refreshToken: number;
  onCourseChange: (courseId: BrowserCourseId) => void;
  onSelectQuestion: (questionId: number, moduleId: number) => void;
  onTreeLoaded?: (tree: QuestionLibraryCourseTree | null) => void;
};

/** Strip a trailing "[content-key vN]" and a repeated course-name prefix from factory module names. */
const moduleDisplayName = (module: QuestionLibraryTreeModule, courseName: string | undefined) => {
  let name = (module.title || module.name || '').trim();
  let version = '';
  const versionMatch = name.match(/\s*\[([^\]]+)\]\s*$/);
  if (versionMatch && versionMatch.index !== undefined) {
    version = versionMatch[1];
    name = name.slice(0, versionMatch.index).trim();
  }
  if (courseName) {
    for (const separator of [' — ', ' | ', ': ']) {
      const prefix = `${courseName}${separator}`.toLowerCase();
      if (name.toLowerCase().startsWith(prefix)) {
        name = name.slice(prefix.length).trim();
      }
    }
  }
  return { name: name || module.name, version };
};

const CourseRow: React.FC<{
  course: QuestionLibraryCatalogCourse;
  active: boolean;
  onPick: () => void;
}> = ({ course, active, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    data-testid={`ql-course-option-${course.id}`}
    className={cn(
      'flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left text-sm hover:bg-foundation-calm-blue-50',
      active && 'bg-foundation-calm-blue-50 font-semibold',
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'h-2.5 w-2.5 shrink-0 rounded-[3px]',
        course.looks_like_test ? 'bg-foundation-base-darkbase-dark-100' : 'bg-foundation-orange-500',
      )}
    />
    <span className="min-w-0 flex-1 truncate">{course.name}</span>
    <span className="shrink-0 text-xs text-muted-foreground">
      {course.module_count} mod · {course.question_count} q
    </span>
  </button>
);

export const QuestionCourseBrowser: React.FC<QuestionCourseBrowserProps> = ({
  courseId,
  openModuleId,
  selectedQuestionId,
  refreshToken,
  onCourseChange,
  onSelectQuestion,
  onTreeLoaded,
}) => {
  const [catalog, setCatalog] = React.useState<QuestionLibraryCourseCatalog | null>(null);
  const [catalogError, setCatalogError] = React.useState<string | null>(null);
  const [tree, setTree] = React.useState<QuestionLibraryCourseTree | null>(null);
  const [treeLoading, setTreeLoading] = React.useState(false);
  const [treeError, setTreeError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [courseSearch, setCourseSearch] = React.useState('');
  const [showTests, setShowTests] = React.useState(false);
  const [treeFilter, setTreeFilter] = React.useState('');
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const pickerButtonRef = React.useRef<HTMLButtonElement>(null);
  const focusRef = React.useRef({ openModuleId, selectedQuestionId });
  focusRef.current = { openModuleId, selectedQuestionId };

  React.useEffect(() => {
    let cancelled = false;
    adminContent.questions
      .courseCatalog()
      .then((response) => {
        if (!cancelled) {
          setCatalog(response.data as QuestionLibraryCourseCatalog);
          setCatalogError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogError('Could not load courses.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  // Default to Life Plan (or the server's fallback) when the URL doesn't name a course.
  React.useEffect(() => {
    if (courseId !== null || !catalog) {
      return;
    }
    const fallback = catalog.default_course_id ?? groupCatalogCourses(catalog.courses).primary[0]?.id ?? null;
    if (fallback !== null) {
      onCourseChange(fallback);
    }
  }, [catalog, courseId, onCourseChange]);

  React.useEffect(() => {
    if (courseId === null) {
      return undefined;
    }
    let cancelled = false;
    setTreeLoading(true);
    adminContent.questions
      .courseTree(courseId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const nextTree = response.data as QuestionLibraryCourseTree;
        // Expand in the same update as the tree so the first paint already shows the right module.
        const { openModuleId: wantedModule, selectedQuestionId: wantedQuestion } = focusRef.current;
        const holder = wantedQuestion
          ? nextTree.modules.find((module) => module.questions.some((question) => question.id === wantedQuestion))
          : undefined;
        const initialModule =
          (wantedModule !== null && nextTree.modules.some((module) => module.id === wantedModule) ? wantedModule : null)
          ?? holder?.id
          ?? nextTree.modules[0]?.id;
        setTree(nextTree);
        setExpanded(initialModule !== undefined ? new Set([initialModule]) : new Set());
        setTreeError(null);
        onTreeLoaded?.(nextTree);
      })
      .catch(() => {
        if (!cancelled) {
          setTree(null);
          setTreeError('Could not load this course.');
          onTreeLoaded?.(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTreeLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, onTreeLoaded, refreshToken]);

  // Later navigation (Prev/Next, deep links) also opens the module that holds the selection.
  React.useEffect(() => {
    if (!tree) {
      return;
    }
    const holder = selectedQuestionId
      ? tree.modules.find((module) => module.questions.some((question) => question.id === selectedQuestionId))
      : undefined;
    const target = openModuleId ?? holder?.id;
    if (target === undefined || target === null) {
      return;
    }
    setExpanded((current) => (current.has(target) ? current : new Set(current).add(target)));
  }, [openModuleId, selectedQuestionId, tree]);

  React.useEffect(() => {
    setTreeFilter('');
  }, [courseId]);

  React.useEffect(() => {
    if (!pickerOpen) {
      return undefined;
    }
    const handlePointer = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPickerOpen(false);
        pickerButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [pickerOpen]);

  const grouped = React.useMemo(() => groupCatalogCourses(catalog?.courses ?? []), [catalog]);
  const activeCourse =
    typeof courseId === 'number' ? catalog?.courses.find((course) => course.id === courseId) : undefined;
  const term = courseSearch.trim().toLowerCase();
  const matches = (course: QuestionLibraryCatalogCourse) => !term || course.name.toLowerCase().includes(term);

  const pickCourse = (next: BrowserCourseId) => {
    setPickerOpen(false);
    setCourseSearch('');
    onCourseChange(next);
  };

  const treeTerm = treeFilter.trim().toLowerCase();
  // Numbers come from the full learner order, so filtering never renumbers modules or steps.
  const visibleModules = (tree?.modules ?? [])
    .map((module, moduleIndex) => {
      const steps = module.questions.map((question, questionIndex) => ({ question, step: questionIndex + 1 }));
      const moduleNumber = moduleIndex + 1;
      if (!treeTerm) {
        return { module, moduleNumber, questions: steps };
      }
      const moduleMatches = (module.title || module.name || '').toLowerCase().includes(treeTerm);
      const questions = moduleMatches
        ? steps
        : steps.filter(
            ({ question }) =>
              plainQuestionLabel(question.label).toLowerCase().includes(treeTerm)
              || question.key.toLowerCase().includes(treeTerm),
          );
      return { module, moduleNumber, questions };
    })
    .filter(({ module, questions }) => !treeTerm || questions.length > 0 || (module.title || module.name).toLowerCase().includes(treeTerm));

  const chapterTitle = new Map((tree?.chapters ?? []).map((chapter) => [chapter.id, chapter.title]));
  let previousChapter: number | null | undefined;

  const toggleModule = (moduleId: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const pickerLabel =
    courseId === UNASSIGNED_COURSE ? 'Modules not in any course' : activeCourse?.name ?? 'Choose a course';
  const pickerMeta =
    courseId === UNASSIGNED_COURSE
      ? `${catalog?.unassigned_module_count ?? 0} modules · ${catalog?.unassigned_question_count ?? 0} questions`
      : activeCourse
        ? `${activeCourse.module_count} modules · ${activeCourse.question_count} questions`
        : '';

  return (
    <div
      className="flex max-h-[32rem] min-h-0 min-w-0 flex-col rounded-3xl bg-white/40 p-3 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)]"
      data-testid="ql-course-browser"
    >
      <div className="relative min-w-0" ref={pickerRef}>
        <button
          type="button"
          ref={pickerButtonRef}
          onClick={() => setPickerOpen((open) => !open)}
          aria-expanded={pickerOpen}
          aria-haspopup="listbox"
          data-testid="ql-course-picker"
          className="flex w-full items-center gap-3 rounded-2xl bg-white px-3 py-2.5 text-left shadow-sm"
        >
          <span
            aria-hidden="true"
            className={cn(
              'grid h-8 w-8 shrink-0 place-items-center rounded-xl text-sm font-bold text-white',
              activeCourse?.looks_like_test || courseId === UNASSIGNED_COURSE
                ? 'bg-foundation-base-darkbase-dark-300'
                : 'bg-foundation-orange-500',
            )}
          >
            {courseId === UNASSIGNED_COURSE ? '?' : (activeCourse?.name.trim()[0] ?? '·').toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-foreground">{pickerLabel}</span>
            {pickerMeta ? <span className="block truncate text-xs text-muted-foreground">{pickerMeta}</span> : null}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
        {pickerOpen ? (
          <div
            className="absolute left-0 top-full z-30 mt-2 flex max-h-[70vh] w-[min(28rem,calc(100vw-3rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            data-testid="ql-course-menu"
          >
            <div className="p-3 pb-2">
              <Input
                autoFocus
                value={courseSearch}
                onChange={(event) => setCourseSearch(event.target.value)}
                placeholder={`Find a course (${catalog?.courses.length ?? 0})`}
                aria-label="Find a course"
              />
            </div>
            <div className="min-h-0 overflow-y-auto px-2 pb-3">
              <p className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Courses <span className="font-normal">{grouped.primary.length}</span>
              </p>
              {grouped.primary.filter(matches).map((course) => (
                <CourseRow key={course.id} course={course} active={course.id === courseId} onPick={() => pickCourse(course.id)} />
              ))}
              {catalog && catalog.unassigned_module_count > 0 && !term ? (
                <>
                  <p className="px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Loose modules
                  </p>
                  <button
                    type="button"
                    onClick={() => pickCourse(UNASSIGNED_COURSE)}
                    data-testid="ql-course-option-unassigned"
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-left text-sm hover:bg-foundation-calm-blue-50',
                      courseId === UNASSIGNED_COURSE && 'bg-foundation-calm-blue-50 font-semibold',
                    )}
                  >
                    <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-[3px] bg-foundation-base-darkbase-dark-300" />
                    <span className="min-w-0 flex-1 truncate">Modules not in any course</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {catalog.unassigned_module_count} mod · {catalog.unassigned_question_count} q
                    </span>
                  </button>
                </>
              ) : null}
              {grouped.tests.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowTests((open) => !open)}
                    aria-expanded={showTests || !!term}
                    data-testid="ql-course-tests-toggle"
                    className="mt-3 flex w-full items-center gap-1.5 px-2.5 pb-1 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {showTests || term ? <ChevronDown className="h-3 w-3" aria-hidden="true" /> : <ChevronRight className="h-3 w-3" aria-hidden="true" />}
                    Looks like a test or old copy <span className="font-normal">{grouped.tests.length}</span>
                  </button>
                  {showTests || term
                    ? grouped.tests.filter(matches).map((course) => (
                        <CourseRow key={course.id} course={course} active={course.id === courseId} onPick={() => pickCourse(course.id)} />
                      ))
                    : null}
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="relative mt-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={treeFilter}
          onChange={(event) => setTreeFilter(event.target.value)}
          placeholder="Find in this course"
          aria-label="Find in this course"
          className="pl-9"
        />
      </div>

      {typeof courseId === 'number' ? <CourseJumpLinks courseId={courseId} current="question-library" className="px-2 pt-2" /> : null}
      <div className="mt-2 min-h-[12rem] flex-1 overflow-y-auto pr-1" data-testid="ql-course-tree">
        {catalogError ? <p className="p-3 text-sm text-destructive">{catalogError}</p> : null}
        {treeError ? <p className="p-3 text-sm text-destructive">{treeError}</p> : null}
        {treeLoading && !tree ? (
          <div className="flex justify-center py-10"><LoadingAnimation /></div>
        ) : null}
        {tree && visibleModules.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            {treeTerm ? 'Nothing in this course matches.' : 'This course has no modules yet.'}
          </p>
        ) : null}
        {visibleModules.map(({ module, moduleNumber, questions }) => {
          const isOpen = expanded.has(module.id) || !!treeTerm;
          const { name, version } = moduleDisplayName(module, tree?.course?.name);
          const showChapter = tree?.chapters.length && module.chapter_id !== previousChapter;
          previousChapter = module.chapter_id;
          return (
            <React.Fragment key={module.id}>
              {showChapter ? (
                <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {module.chapter_id ? chapterTitle.get(module.chapter_id) : 'Not in a chapter'}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => toggleModule(module.id)}
                aria-expanded={isOpen}
                title={module.name}
                data-testid={`ql-module-${module.id}`}
                className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm font-semibold text-foreground hover:bg-white/60"
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-foundation-base-darkbase-dark-500/5 text-[11px] font-medium text-muted-foreground">
                  {moduleNumber}
                </span>
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {version ? <span className="shrink-0 rounded-full bg-foundation-base-darkbase-dark-500/5 px-1.5 text-[10.5px] font-normal text-muted-foreground">{version}</span> : null}
                <span className="shrink-0 text-xs font-normal text-muted-foreground">{module.question_count}</span>
              </button>
              {isOpen ? (
                <ul className="mb-1">
                  {questions.map(({ question, step }) => {
                    const label = plainQuestionLabel(question.label);
                    const isSelected = question.id === selectedQuestionId;
                    return (
                      <li key={`${module.id}-${question.id}-${question.order}`}>
                        <button
                          type="button"
                          onClick={() => onSelectQuestion(question.id, module.id)}
                          aria-current={isSelected ? 'true' : undefined}
                          title={`${formatQuestionTypeSlug(question.type)} · ${question.key}`}
                          data-testid={`ql-tree-question-${question.id}`}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-xl py-1 pl-8 pr-2 text-left text-[13px] hover:bg-white/60',
                            isSelected ? 'bg-foundation-orange-50 font-medium text-foreground' : 'text-foundation-base-darkbase-dark-500',
                            !question.is_active && 'opacity-60',
                          )}
                        >
                          <span className="w-4 shrink-0 text-right text-[11px] text-muted-foreground">{step}</span>
                          <span className="min-w-0 flex-1 truncate">
                            {label || <span className="italic text-destructive">(empty) {question.key}</span>}
                          </span>
                          {question.used_in_count > 1 ? (
                            <span
                              className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-blue-50 px-1.5 text-[10.5px] text-blue-700"
                              title={`Used in ${question.used_in_count} modules`}
                            >
                              <Link2 className="h-3 w-3" aria-hidden="true" />×{question.used_in_count}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
      {courseId === UNASSIGNED_COURSE ? (
        <p className="px-2 pt-2 text-xs text-muted-foreground">
          These modules aren’t attached to any course.
        </p>
      ) : null}
      {activeCourse?.looks_like_test ? (
        <p className="px-2 pt-2 text-xs text-muted-foreground">This course looks like a test or old copy.</p>
      ) : null}
    </div>
  );
};

export default QuestionCourseBrowser;
